import RouteNames from '@/configuration/router/names.routes';
import EventConstants from '@/core/eventConstants';
import { StorageKeys } from '@/models/storage-keys';
import eventEmitter from '@/utils/eventEmitter';
import { StorageUtils } from '@/utils/storage.utils';
import { reactive } from 'vue';
import { RouteLocationNormalized, RouteLocationRaw, Router } from 'vue-router';

class NavigationService {
  private router: Router | null = null;
  private routeHistory: string[] = reactive([]);

  constructor() {
    eventEmitter.on(EventConstants.LOGOUT_EVENT, () => {
      console.debug('NavigationService: clearHistory called on logout event');
      this.clearHistory();
    });
  }

  /**
   * Sets the router.
   * @param {Router} router - The router to set.
   */
  public setRouter(router: Router) {
    this.router = router;
  }

  /**
   * Navigates to a specified route.
   * @param {RouteLocationRaw} route - The route to navigate to.
   */
  public navigateTo(
    route: RouteLocationRaw,
    options: { replace?: boolean } = {}
  ): void {
    if (!this.router) {
      console.error('El router no está configurado en NavigationService.');
      return;
    }

    const { replace = false } = options;

    const routePath: string = this._getRoutePath(route);

    const navigatingToHome = routePath === RouteNames.Home;

    if (navigatingToHome) {
      this.clearHistory();
      this.router.push(route);
    } else {
      if (replace && this.routeHistory.length > 0) {
        this._removeLastFromHistory();
      }
      this.router[replace ? 'replace' : 'push'](route);
      this._addToHistory(routePath);
    }
  }

  /**
   * Navigates back to the previous route.
   */
  public goBack(): void {
    if (this.routeHistory.length > 1) {
      this._removeLastFromHistory();
      const previousRoute = this.routeHistory[this.routeHistory.length - 1];
      if (this.router) {
        this.router.push({ name: previousRoute });
      }
    } else {
      this.navigateTo({ name: RouteNames.Home });
    }
  }

  /**
   * Clears the route history.
   */
  public clearHistory(): void {
    this.routeHistory.length = 0;
    this._persistOnStorage();
  }

  /**
   * Gets the route history.
   * @returns {string[]} The route history.
   */
  public getHistory(): string[] {
    return [...this.routeHistory];
  }

  /**
   * Gets the current route.
   * @returns {RouteLocationNormalized | { name: string }} The current route.
   */
  public getCurrentRoute(): RouteLocationNormalized | { name: string } {
    if (!this.router) {
      console.error('El router no está configurado en NavigationService.');
      return { name: 'InvalidRoute' };
    }
    return this.router.currentRoute.value;
  }

  public async redirectToFirstRoute(): Promise<void> {
    /* const storedPath =
      (await StorageUtils.getStorageItem(StorageKeys.NAV_HISTORY)) || '';
    this.routeHistory = storedPath.split('/');

    const route = this.routeHistory[this.routeHistory.length - 1] ?? '';

    if (route) {
      this.navigateTo({ name: route });
    } else {
      this.navigateTo({ name: RouteNames.Home });
    } */
    this.clearHistory();
    this.navigateTo({ name: RouteNames.Home });
  }

  /**
   * Navigates to a stored route.
   * @param {string} storedPath - The stored path to navigate to.
   */
  public navigateToStoredRoute(storedPath: string): void {
    this.routeHistory = storedPath.split('/');

    const route = this.routeHistory[this.routeHistory.length - 1] ?? '';

    this.navigateTo({ name: route });
  }

  /**
   * Gets the path of a route.
   * @param {RouteLocationRaw} route - The route to get the path from.
   * @returns {string} The path of the route.
   */
  private _getRoutePath(route: RouteLocationRaw): string {
    if (typeof route === 'string') {
      return route;
    } else if ('path' in route) {
      return route.path || '/';
    } else if ('name' in route && typeof route.name === 'string') {
      return route.name;
    }
    return '/';
  }

  /**
   * Adds a route to the history if it's not the same as the last one.
   * @param {string} route - The route to add to the history.
   *
   * @private
   */
  private _addToHistory(route: string): void {
    if (this.routeHistory[this.routeHistory.length - 1] !== route) {
      this.routeHistory.push(route);
    }
    this._persistOnStorage();
  }

  /**
   * Removes the last route from the history.
   *
   * @private
   */
  private _removeLastFromHistory(): void {
    this.routeHistory.pop();
    this._persistOnStorage();
  }

  /**
   * Persists the current navigation history to storage if the `rememberNavigationHistory` flag is set to true.
   * The navigation history is stored as a single string with routes joined by '/'.
   *
   * @private
   */
  private _persistOnStorage(): void {
    StorageUtils.setStorageItem(
      StorageKeys.NAV_HISTORY,
      this.routeHistory.join('/')
    );
  }
}

const navigationService = new NavigationService();
export default navigationService;
